// src/components/SettingsPanel.tsx
import React from 'react';
import { DashboardSettings, BlockchainNetwork } from '../types';
import { CHART_TIMEFRAMES, BLOCKCHAIN_NETWORKS } from '../constants';

interface SettingsPanelProps {
  settings: DashboardSettings;
  onClose: () => void;
  onUpdate: (newSettings: Partial<DashboardSettings>) => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  settings,
  onClose,
  onUpdate
}) => {
  // Handler for theme change
  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      displayOptions: {
        ...settings.displayOptions,
        theme: e.target.value as 'light' | 'dark' | 'system'
      }
    });
  };

  // Handler for layout change
  const handleLayoutChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      displayOptions: {
        ...settings.displayOptions,
        layout: e.target.value as 'grid' | 'list'
      }
    });
  };

  // Handler for density change
  const handleDensityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      displayOptions: {
        ...settings.displayOptions,
        density: e.target.value as 'compact' | 'comfortable' | 'spacious'
      }
    });
  };

  // Handler for chart timeframe change
  const handleTimeframeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      displayOptions: {
        ...settings.displayOptions,
        chartTimeframe: e.target.value as '1d' | '1w' | '1m' | '3m' | '6m' | '1y' | 'all'
      }
    });
  };

  // Handler for refresh rate changes
  const handleRefreshRateChange = (
    dataType: keyof DashboardSettings['refreshRates'],
    value: string
  ) => {
    onUpdate({
      refreshRates: {
        ...settings.refreshRates,
        [dataType]: parseInt(value)
      }
    });
  };

  // Handler for alert threshold changes
  const handleThresholdChange = (
    thresholdType: keyof DashboardSettings['alertThresholds'],
    value: string
  ) => {
    onUpdate({
      alertThresholds: {
        ...settings.alertThresholds,
        [thresholdType]: parseInt(value)
      }
    });
  };

  // Handler for wallet tracking settings
  const handleWalletTrackingChange = (
    settingType: keyof DashboardSettings['walletTracking'],
    value: any
  ) => {
    onUpdate({
      walletTracking: {
        ...settings.walletTracking,
        [settingType]: value
      }
    });
  };

  // Handler for network selection
  const handleNetworkChange = (network: BlockchainNetwork, checked: boolean) => {
    const updatedNetworks = checked
      ? [...settings.walletTracking.trackedNetworks, network]
      : settings.walletTracking.trackedNetworks.filter(n => n !== network);
    
    handleWalletTrackingChange('trackedNetworks', updatedNetworks);
  };

  return (
    <div className="settings-panel open p-5 overflow-y-auto">
      <div className="settings-header flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Dashboard Settings</h2>
        <button
          onClick={onClose}
          className="settings-close-btn"
          aria-label="Close settings"
        >
          ×
        </button>
      </div>

      <div className="settings-content space-y-6">
        {/* Display Options */}
        <section>
          <h3 className="text-lg font-semibold mb-4">Display Options</h3>
          
          <div className="form-group">
            <label className="form-label" htmlFor="theme">Theme</label>
            <select
              id="theme"
              className="form-select"
              value={settings.displayOptions.theme}
              onChange={handleThemeChange}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="layout">Layout</label>
            <select
              id="layout"
              className="form-select"
              value={settings.displayOptions.layout}
              onChange={handleLayoutChange}
            >
              <option value="grid">Grid</option>
              <option value="list">List</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="density">Density</label>
            <select
              id="density"
              className="form-select"
              value={settings.displayOptions.density}
              onChange={handleDensityChange}
            >
              <option value="compact">Compact</option>
              <option value="comfortable">Comfortable</option>
              <option value="spacious">Spacious</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="chartTimeframe">Chart Timeframe</label>
            <select
              id="chartTimeframe"
              className="form-select"
              value={settings.displayOptions.chartTimeframe}
              onChange={handleTimeframeChange}
            >
              {CHART_TIMEFRAMES.map(timeframe => (
                <option key={timeframe.value} value={timeframe.value}>
                  {timeframe.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* Refresh Rates */}
        <section>
          <h3 className="text-lg font-semibold mb-4">Refresh Rates</h3>
          
          <div className="form-group">
            <label className="form-label" htmlFor="marketDataRefresh">
              Market Data (ms)
            </label>
            <input
              id="marketDataRefresh"
              type="number"
              className="form-input"
              value={settings.refreshRates.marketData}
              onChange={(e) => handleRefreshRateChange('marketData', e.target.value)}
              min="5000"
              step="1000"
            />
          </div>
          
          {/* Removed Trading Data (ms) form group */}
          
          <div className="form-group">
            <label className="form-label" htmlFor="newsDataRefresh">
              News Data (ms)
            </label>
            <input
              id="newsDataRefresh"
              type="number"
              className="form-input"
              value={settings.refreshRates.newsData}
              onChange={(e) => handleRefreshRateChange('newsData', e.target.value)}
              min="5000"
              step="1000"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="walletDataRefresh">
              Wallet Data (ms)
            </label>
            <input
              id="walletDataRefresh"
              type="number"
              className="form-input"
              value={settings.refreshRates.walletData}
              onChange={(e) => handleRefreshRateChange('walletData', e.target.value)}
              min="5000"
              step="1000"
            />
          </div>
        </section>

        {/* Alert Thresholds */}
        <section>
          <h3 className="text-lg font-semibold mb-4">Alert Thresholds</h3>
          
          <div className="form-group">
            <label className="form-label" htmlFor="marketHeatThreshold">
              Market Heat Alert ({settings.alertThresholds.marketHeat})
            </label>
            <input
              id="marketHeatThreshold"
              type="range"
              className="form-slider w-full"
              min="0"
              max="100"
              value={settings.alertThresholds.marketHeat}
              onChange={(e) => handleThresholdChange('marketHeat', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="rsiOverboughtThreshold">
              RSI Overbought ({settings.alertThresholds.rsiOverbought})
            </label>
            <input
              id="rsiOverboughtThreshold"
              type="range"
              className="form-slider w-full"
              min="50"
              max="90"
              value={settings.alertThresholds.rsiOverbought}
              onChange={(e) => handleThresholdChange('rsiOverbought', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="rsiOversoldThreshold">
              RSI Oversold ({settings.alertThresholds.rsiOversold})
            </label>
            <input
              id="rsiOversoldThreshold"
              type="range"
              className="form-slider w-full"
              min="10"
              max="50"
              value={settings.alertThresholds.rsiOversold}
              onChange={(e) => handleThresholdChange('rsiOversold', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="walletMovementThreshold">
              Significant Wallet Movement % ({settings.alertThresholds.significantWalletMovement})
            </label>
            <input
              id="walletMovementThreshold"
              type="range"
              className="form-slider w-full"
              min="1"
              max="50"
              value={settings.alertThresholds.significantWalletMovement}
              onChange={(e) => handleThresholdChange('significantWalletMovement', e.target.value)}
            />
          </div>
        </section>

        {/* Wallet Tracking */}
        <section>
          <h3 className="text-lg font-semibold mb-4">Wallet Tracking</h3>
          
          <div className="form-group">
            <label className="form-label" htmlFor="minWalletAge">
              Minimum Wallet Age (days)
            </label>
            <input
              id="minWalletAge"
              type="number"
              className="form-input"
              value={settings.walletTracking.minAgeInDays}
              onChange={(e) => handleWalletTrackingChange('minAgeInDays', parseInt(e.target.value))}
              min="1"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="minWalletBalance">
              Minimum Balance (USD)
            </label>
            <input
              id="minWalletBalance"
              type="number"
              className="form-input"
              value={settings.walletTracking.minBalanceUSD}
              onChange={(e) => handleWalletTrackingChange('minBalanceUSD', parseInt(e.target.value))}
              min="1000"
              step="1000"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Tracked Networks</label>
            <div className="space-y-2 mt-2">
              {BLOCKCHAIN_NETWORKS.map((network) => (
                <div key={network} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`network-${network}`}
                    checked={settings.walletTracking.trackedNetworks.includes(network as BlockchainNetwork)}
                    onChange={(e) => handleNetworkChange(network as BlockchainNetwork, e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor={`network-${network}`}>{network}</label>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsPanel;
